import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import FieldHelpTooltip from "../../../../../components/common/FieldHelpTooltip";
import { PartyAddress, ServiceCategory } from "../../../../types/types";
import { ServiceFormValue } from "../serviceFormTypes";
interface DetailsStepProps {
  value: ServiceFormValue;
  partyAddresses: PartyAddress[];
  categories: ServiceCategory[];
  onChange: (value: ServiceFormValue) => void;
  onCategoryChange: (id: number | null) => void;
  onSubcategoryToggle: (id: number) => void;
}

export function ServiceDetailsStep({
  value,
  partyAddresses,
  categories,
  onChange,
  onCategoryChange,
  onSubcategoryToggle,
}: DetailsStepProps) {
  const update = (changes: Partial<ServiceFormValue>) =>
    onChange({ ...value, ...changes });

  return (
    <div className="card">
      <div className="card-title">Details</div>
      <div className="form-group">
        <label className="form-label" htmlFor="service-kind">Type</label>
        <select
          id="service-kind"
          className="form-input"
          value={value.serviceKind}
          onChange={(event) =>
            update({ serviceKind: event.target.value as "Service" | "Job" })
          }
        >
          <option value="Service">Service</option>
          <option value="Job">Job</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">
          Name <span className="service-form-required-marker">*</span>
        </label>
        <input
          className="form-input"
          value={value.name}
          onChange={(event) => update({ name: event.target.value })}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-input"
          value={value.description}
          onChange={(event) => update({ description: event.target.value })}
          rows={3}
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="service-address">
          Address
          <FieldHelpTooltip text="Select the address where this service will run. If the address is not listed, add it to the organisation first." />
        </label>
        <select
          id="service-address"
          className="form-input"
          value={value.partyAddressId ?? ""}
          onChange={(event) =>
            update({
              partyAddressId: event.target.value
                ? Number(event.target.value)
                : null,
            })
          }
        >
          <option value="">No service address</option>
          {partyAddresses.map(({ partyAddressId, isPrimary, address }) => {
            const label =
              address.fullAddress ||
              [address.addressLine1, address.town, address.postCode]
                .filter(Boolean)
                .join(", ");
            return (
              <option key={partyAddressId} value={partyAddressId}>
                {isPrimary ? "Primary — " : ""}
                {label}
              </option>
            );
          })}
        </select>
      </div>
      <ServiceCategoryFields
        value={value}
        categories={categories}
        onCategoryChange={onCategoryChange}
        onSubcategoryToggle={onSubcategoryToggle}
      />
    </div>
  );
}

interface CategoryFieldsProps {
  value: ServiceFormValue;
  categories: ServiceCategory[];
  onCategoryChange: (id: number | null) => void;
  onSubcategoryToggle: (id: number) => void;
}

function ServiceCategoryFields({
  value,
  categories,
  onCategoryChange,
  onSubcategoryToggle,
}: CategoryFieldsProps) {
  const selectedCategory =
    categories.find(
      (category) => category.serviceCategoryId === value.serviceCategoryId,
    ) ?? null;

  if (!categories.length) {
    return (
      <div className="service-classification-unavailable" role="status">
        Service categories will appear here when the classification catalogue is
        available.
      </div>
    );
  }

  return (
    <>
      <div className="form-group">
        <label className="form-label" htmlFor="service-category">
          Category{" "}
          <span className="service-form-required-marker">*</span>
          <FieldHelpTooltip text="Choose the category that best describes the service's main purpose. You can then select up to two more specific subcategories." />
        </label>
        <select
          id="service-category"
          className="form-input"
          value={value.serviceCategoryId ?? ""}
          onChange={(event) =>
            onCategoryChange(
              event.target.value ? Number(event.target.value) : null,
            )
          }
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option
              key={category.serviceCategoryId}
              value={category.serviceCategoryId}
            >
              {category.name}
            </option>
          ))}
        </select>
      </div>
      {selectedCategory && (
        <div className="form-group">
          <div className="service-subcategory-heading">
            <div>
              <div className="form-label">Subcategories</div>
              <div className="field-hint">Optional · choose up to 2</div>
            </div>
            <span className="service-classification-count">
              {value.serviceSubcategoryIds.length} of 2
            </span>
          </div>
          {selectedCategory.subcategories.length ? (
            <div className="service-subcategory-options">
              {selectedCategory.subcategories.map((subcategory) => {
                const selected = value.serviceSubcategoryIds.includes(
                  subcategory.serviceSubcategoryId,
                );
                return (
                  <button
                    type="button"
                    key={subcategory.serviceSubcategoryId}
                    className={`service-subcategory-option ${selected ? "selected" : ""}`}
                    disabled={
                      !selected && value.serviceSubcategoryIds.length >= 2
                    }
                    aria-pressed={selected}
                    onClick={() =>
                      onSubcategoryToggle(subcategory.serviceSubcategoryId)
                    }
                  >
                    <span
                      className="service-subcategory-check"
                      aria-hidden="true"
                    >
                      {selected && <CheckIcon />}
                    </span>
                    <span className="service-subcategory-copy">
                      {subcategory.name}
                      {subcategory.description && (
                        <small>{subcategory.description}</small>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="service-subcategory-empty">
              No subcategories are configured for this category.
            </div>
          )}
        </div>
      )}
    </>
  );
}
